import { useState, useEffect } from 'react';
import { SearchableSelect } from './SearchableSelect';
import { useGetMerchants } from '../api/useGetMerchants';

interface Option {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
}

export const MerchantsDropdown = ({ value, onChange }: Props) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  useEffect(() => {
    if (!search) {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => { setDebouncedSearch(search); }, 1000);
    return () => { clearTimeout(timer); };
  }, [search]);

  const { data, isLoading } = useGetMerchants({ q: debouncedSearch || undefined });

  const { merchants = [] } = data?.payload ?? {};

  const options = merchants.map((merchant) => ({
    value: merchant.uid,
    label: `${merchant.name} (${merchant.email})`,
  }));

  const mergedOptions =
    selectedOption && !options.find((o) => o.value === selectedOption.value)
      ? [selectedOption, ...options]
      : options;

  const handleChange = (v: string) => {
    if (!v) { setSelectedOption(null); onChange(''); return; }
    const found = options.find((o) => o.value === v);
    if (found) { setSelectedOption(found); }
    onChange(v);
  };

  return (
    <SearchableSelect
      options={mergedOptions}
      value={value}
      onChange={handleChange}
      onSearchChange={setSearch}
      placeholder="Select a merchant"
      isLoading={isLoading}
    />
  );
};
